import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, User, Check, LifeBuoy, X, Bot } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Atenea from './Atenea';
import SupportPanel from './SupportPanel';

import { API } from '../config';
const o = { withCredentials: true };

const ICONS = {
  trade: '📊', emergency: '🚨', investment_exhausted: '💸',
  kyc: '🪪', referral: '👥', payment: '💳', arbitrage: '⚡', soporte: '🎫',
};

// Rutas donde llevar cada tipo de notificación al hacer click
const NOTIF_ROUTES = {
  arbitrage: '/arbitrage',
  trade: '/history',
  emergency: '/bots',
  investment_exhausted: '/cuenta',
  kyc: '/kyc',
  referral: '/cuenta',
  payment: '/cuenta',
  paper_exhausted: '/cuenta',
  paper_low_warning: '/cuenta',
  low_balance_warning: '/cuenta',
  training: '/bots',
};

const TopBar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/notifications`, o);
      setNotifs(r.data.notifications || []);
      setUnread(r.data.unread || 0);
    } catch {}
  };

  useEffect(() => {
    if (user?.account_status === 'active' && user?.kyc_status === 'verified') {
      load();
      const id = setInterval(load, 5000);
      return () => clearInterval(id);
    }
  }, [user?.account_status, user?.kyc_status]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const clearAll = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, o);
    } catch {}
    setUnread(0);
    setNotifs([]);
  };

  return (
    <div className="h-16 bg-[#121212] border-b border-[#27272A] px-6 flex items-center justify-between">
      <div>
        <h1 className="text-xl font-bold text-white tracking-tight">AEGIS IA</h1>
        <p className="text-xs text-[#A1A1AA]">Multi-Exchange Trading Platform</p>
      </div>

      <div className="flex items-center space-x-4">
        <div className="relative" ref={ref}>
          <button onClick={() => { setOpen(!open); if (!open) load(); }}
            className="w-10 h-10 rounded-sm bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center hover:border-[#00D4FF] transition-all relative">
            <Bell size={18} className="text-[#A1A1AA]" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>

          {open && (
            <div className="absolute right-0 top-12 w-80 max-h-96 bg-[#121212] border border-[#27272A] rounded-lg shadow-xl z-50 overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[#27272A]">
                <p className="text-sm font-bold text-white">Notificaciones</p>
                {unread > 0 && (
                  <button onClick={clearAll} className="flex items-center gap-1 text-[10px] text-[#00D4FF] hover:underline">
                    <Check size={12} /> Limpiar todo
                  </button>
                )}
              </div>
              <div className="overflow-y-auto max-h-80">
                {notifs.length === 0 ? (
                  <p className="text-[#A1A1AA] text-sm text-center py-6">Sin notificaciones</p>
                ) : notifs.map((n, i) => {
                  const ruta = NOTIF_ROUTES[n.type];
                  return (
                  <div key={i}
                    onClick={() => { if (ruta) { setOpen(false); navigate(ruta); } }}
                    className={`px-3 py-2 border-b border-[#27272A]/50 transition-colors
                      ${!n.read ? 'bg-[#1E1E1E]' : ''}
                      ${ruta ? 'cursor-pointer hover:bg-[#27272A]' : ''}`}>
                    <div className="flex items-start gap-2">
                      <span className="text-sm mt-0.5">{ICONS[n.type] || '🔔'}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs ${!n.read ? 'text-white font-medium' : 'text-[#A1A1AA]'}`}>{n.message}</p>
                        <p className="text-[10px] text-[#52525B] mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                        {ruta && <p className="text-[10px] text-[#00D4FF] mt-0.5">Toca para ir →</p>}
                      </div>
                    </div>
                  </div>
                );})}
              </div>
            </div>
          )}
        </div>

        <ProfileMenu user={user} />
      </div>
    </div>
  );
};

const ProfileMenu = ({ user }) => {
  const navigate = useNavigate();
  const [openProfile, setOpenProfile] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [ateneaOpen, setAteneaOpen] = useState(false);
  const profRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (profRef.current && !profRef.current.contains(e.target)) { setOpenProfile(false); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="relative" ref={profRef}>
      <button onClick={() => { setOpenProfile(!openProfile); setShowSupport(false); }}
        className="flex items-center space-x-3 px-3 py-2 rounded-sm bg-[#1E1E1E] border border-[#27272A] hover:border-[#3F3F46] transition-all cursor-pointer">
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-[#00D4FF] flex items-center justify-center">
            <User size={16} className="text-black" />
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-white">{user?.name}</p>
          <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">{user?.kyc_status}</p>
        </div>
      </button>

      {openProfile && !showSupport && (
        <div className="absolute right-0 top-14 w-56 bg-[#121212] border border-[#27272A] rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-[#27272A]">
            <p className="text-sm text-white font-medium">{user?.name}</p>
            <p className="text-[10px] text-[#A1A1AA]">{user?.email}</p>
          </div>
          <button onClick={() => { setOpenProfile(false); setAteneaOpen(true); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#A1A1AA] hover:bg-[#1E1E1E] hover:text-white transition-all">
            <Bot size={15} /> Atenea IA
          </button>
          <button onClick={() => setShowSupport(true)}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#A1A1AA] hover:bg-[#1E1E1E] hover:text-white transition-all">
            <LifeBuoy size={15} /> Soporte
          </button>
        </div>
      )}

      {openProfile && showSupport && (
        <div className="absolute right-0 top-14 w-80 max-h-[70vh] bg-[#121212] border border-[#27272A] rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#27272A]">
            <button onClick={() => setShowSupport(false)} className="text-xs text-[#A1A1AA] hover:text-white">← Volver</button>
            <p className="text-sm font-bold text-white">Soporte</p>
            <button onClick={() => { setOpenProfile(false); setShowSupport(false); }}><X size={14} className="text-[#A1A1AA]" /></button>
          </div>
          <div className="p-3 overflow-y-auto max-h-[55vh]">
            <SupportPanel compact />
          </div>
        </div>
      )}

      {ateneaOpen && (
        <Atenea defaultOpen hideOnClose onClosed={() => setAteneaOpen(false)} />
      )}
    </div>
  );
};

export default TopBar;
